import React, { useEffect, useState } from 'react';
import { Container, Table, Button, Modal, Form, Badge, Spinner, Row, Col, Tab, Tabs, InputGroup } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import useProductsStore from '../../store/productsStore';
import MultipleImageUpload from '../../components/MultipleImageUpload';
import { getImageUrl } from '../../utils/imageUrl';
import { categoriesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import AdminLayout from '../../components/AdminLayout';

const Products = () => {
  const { products, fetchProducts, createProduct, updateProduct, deleteProduct, isLoading } = useProductsStore();
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    shortDescription: '',
    image: '',
    images: [],
    basePrice: '',
    originalPrice: '',
    downloadUrl: '',
    featured: false,
    priceTiers: [],
    youtubeUrl: '',
    badges: {
      isNew: false,
      isPopular: false,
      isBestSeller: false,
    },
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: [],
    },
  });

  useEffect(() => {
    fetchProducts({ limit: 100 });
    fetchCategories();
  }, [fetchProducts]);

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getActive();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handleShowModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        category: product.category,
        description: product.description,
        shortDescription: product.shortDescription,
        image: product.image,
        images: product.images || [],
        basePrice: product.basePrice,
        originalPrice: product.originalPrice || '',
        downloadUrl: product.downloadUrl || '',
        featured: product.featured,
        priceTiers: product.priceTiers || [],
        youtubeUrl: product.youtubeUrl || '',
        badges: product.badges || { isNew: false, isPopular: false, isBestSeller: false },
        seo: product.seo || { metaTitle: '', metaDescription: '', keywords: [] },
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: categories.length > 0 ? categories[0].name : '',
        description: '',
        shortDescription: '',
        image: '',
        images: [],
        basePrice: '',
        originalPrice: '',
        downloadUrl: '',
        featured: false,
        priceTiers: [],
        youtubeUrl: '',
        badges: { isNew: false, isPopular: false, isBestSeller: false },
        seo: { metaTitle: '', metaDescription: '', keywords: [] },
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Set main image to first image if images array exists
    const submitData = {
      ...formData,
      image: formData.images[0] || formData.image,
    };

    try {
      if (editingProduct) {
        await updateProduct(editingProduct._id, submitData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(submitData);
        toast.success('Product created successfully');
      }
      setShowModal(false);
      fetchProducts({ limit: 100 });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        toast.success('Product deleted successfully');
        fetchProducts({ limit: 100 });
      } catch (error) {
        toast.error('Failed to delete product');
      }
    }
  };

  // Price Tier handlers
  const addPriceTier = () => {
    setFormData({
      ...formData,
      priceTiers: [...formData.priceTiers, { quantity: '', pricePerUnit: '' }],
    });
  };

  const updatePriceTier = (index, field, value) => {
    const newTiers = [...formData.priceTiers];
    newTiers[index][field] = value;
    setFormData({ ...formData, priceTiers: newTiers });
  };

  const removePriceTier = (index) => {
    const newTiers = formData.priceTiers.filter((_, i) => i !== index);
    setFormData({ ...formData, priceTiers: newTiers });
  };

  // Keywords handler
  const [keywordInput, setKeywordInput] = useState('');

  const addKeyword = () => {
    if (keywordInput.trim()) {
      setFormData({
        ...formData,
        seo: {
          ...formData.seo,
          keywords: [...formData.seo.keywords, keywordInput.trim()],
        },
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (index) => {
    setFormData({
      ...formData,
      seo: {
        ...formData.seo,
        keywords: formData.seo.keywords.filter((_, i) => i !== index),
      },
    });
  };

  return (
    <AdminLayout>
      <Container fluid className="my-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Product Management</h1>
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FaPlus /> Add Product
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Badges</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product._id}>
                  <td>
                    <img 
                      src={getImageUrl(product.images?.[0] || product.image)} 
                      alt={product.name} 
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }} 
                    />
                  </td>
                  <td>{product.name}</td>
                  <td><Badge bg="info">{product.category}</Badge></td>
                  <td>
                    {product.originalPrice && (
                      <span className="text-decoration-line-through text-muted small me-1">
                        ₹{product.originalPrice}
                      </span>
                    )}
                    <span>₹{product.basePrice}</span>
                  </td>
                  <td>{product.stockCount}</td>
                  <td>
                    {product.badges?.isNew && <Badge bg="success" className="me-1">New</Badge>}
                    {product.badges?.isPopular && <Badge bg="warning" className="me-1">Popular</Badge>}
                    {product.badges?.isBestSeller && <Badge bg="danger" className="me-1">Best</Badge>}
                  </td>
                  <td>{product.featured ? <Badge bg="warning">Yes</Badge> : <Badge bg="secondary">No</Badge>}</td>
                  <td>
                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleShowModal(product)}>
                      <FaEdit />
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(product._id)}>
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" scrollable>
          <Modal.Header closeButton>
            <Modal.Title>{editingProduct ? 'Edit Product' : 'Add Product'}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh' }}>
            <Form onSubmit={handleSubmit}>
              <Tabs defaultActiveKey="basic" className="mb-3">
                
                {/* Basic Information Tab */}
                <Tab eventKey="basic" title="Basic Info">
                  <Form.Group className="mb-3">
                    <Form.Label>Product Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                    >
                      <option value="">Select a category...</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Short Description *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.shortDescription}
                      onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                      required
                      maxLength={200}
                    />
                    <Form.Text className="text-muted">
                      {formData.shortDescription.length}/200 characters
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Full Description *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Download URL (Optional)</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.downloadUrl}
                      onChange={(e) => setFormData({ ...formData, downloadUrl: e.target.value })}
                      placeholder="https://drive.google.com/..."
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Featured Product"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    />
                  </Form.Group>
                </Tab>

                {/* Images Tab */}
                <Tab eventKey="images" title="Images">
                  <MultipleImageUpload
                    currentImages={formData.images}
                    onImagesChange={(images) => setFormData({ ...formData, images })}
                    maxImages={10}
                  />
                </Tab>

                {/* Pricing Tab */}
                <Tab eventKey="pricing" title="Pricing">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Base Price (₹) *</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.basePrice}
                          onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
                          required
                          min="0"
                          step="0.01"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Original Price (₹) - Optional</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.originalPrice}
                          onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                          min="0"
                          step="0.01"
                          placeholder="For strike-through display"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />

                  <h6>Quantity-Based Pricing Tiers</h6>
                  <p className="text-muted small">Add bulk pricing discounts</p>

                  {formData.priceTiers.map((tier, index) => (
                    <Row key={index} className="mb-2">
                      <Col md={5}>
                        <Form.Control
                          type="number"
                          placeholder="Quantity"
                          value={tier.quantity}
                          onChange={(e) => updatePriceTier(index, 'quantity', e.target.value)}
                          min="1"
                        />
                      </Col>
                      <Col md={5}>
                        <Form.Control
                          type="number"
                          placeholder="Price per unit"
                          value={tier.pricePerUnit}
                          onChange={(e) => updatePriceTier(index, 'pricePerUnit', e.target.value)}
                          min="0"
                          step="0.01"
                        />
                      </Col>
                      <Col md={2}>
                        <Button variant="danger" size="sm" onClick={() => removePriceTier(index)}>
                          <FaMinus />
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  <Button variant="outline-primary" size="sm" onClick={addPriceTier}>
                    <FaPlus /> Add Price Tier
                  </Button>
                </Tab>

                {/* Marketing Tab */}
                <Tab eventKey="marketing" title="Marketing">
                  <Form.Group className="mb-3">
                    <Form.Label>YouTube Video URL (Optional)</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.youtubeUrl}
                      onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <Form.Text className="text-muted">
                      Product demo or tutorial video
                    </Form.Text>
                  </Form.Group>

                  <hr />

                  <h6>Product Badges</h6>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="New Product"
                      checked={formData.badges.isNew}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          badges: { ...formData.badges, isNew: e.target.checked },
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="Popular"
                      checked={formData.badges.isPopular}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          badges: { ...formData.badges, isPopular: e.target.checked },
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="Best Seller"
                      checked={formData.badges.isBestSeller}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          badges: { ...formData.badges, isBestSeller: e.target.checked },
                        })
                      }
                    />
                  </Form.Group>
                </Tab>

                {/* SEO Tab */}
                <Tab eventKey="seo" title="SEO">
                  <Form.Group className="mb-3">
                    <Form.Label>Meta Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.seo.metaTitle}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seo: { ...formData.seo, metaTitle: e.target.value },
                        })
                      }
                      placeholder="Leave empty to use product name"
                      maxLength={60}
                    />
                    <Form.Text className="text-muted">
                      {formData.seo.metaTitle.length}/60 characters (optimal for Google)
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Meta Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.seo.metaDescription}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seo: { ...formData.seo, metaDescription: e.target.value },
                        })
                      }
                      placeholder="Leave empty to use short description"
                      maxLength={160}
                    />
                    <Form.Text className="text-muted">
                      {formData.seo.metaDescription.length}/160 characters (optimal for Google)
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Keywords</Form.Label>
                    <InputGroup className="mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Add a keyword"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addKeyword();
                          }
                        }}
                      />
                      <Button variant="outline-secondary" onClick={addKeyword}>
                        Add
                      </Button>
                    </InputGroup>
                    <div>
                      {formData.seo.keywords.map((keyword, index) => (
                        <Badge
                          key={index}
                          bg="secondary"
                          className="me-2 mb-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => removeKeyword(index)}
                        >
                          {keyword} ×
                        </Badge>
                      ))}
                    </div>
                  </Form.Group>
                </Tab>

              </Tabs>

              <div className="d-grid gap-2 mt-4">
                <Button type="submit" variant="primary" size="lg">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </AdminLayout>
  );
};

export default Products;
